import React, { useState, useContext, useEffect } from 'react';
import cheksteSymbol from '../assets/chekste_symbol_web.png';
import { useNavigate, useLocation } from 'react-router-dom';
import { UserContext } from '../App';
import { useTranslation } from '../i18n';
import { authAPI, adminProfitabilityAPI } from '../api';
import CompactUsageCounter from './CompactUsageCounter';
import SignInDialog from './SignInDialog';
import { 
  LayoutDashboard, 
  Search, 
  Bookmark, 
  GitCompare, 
  Inbox, 
  CreditCard, 
  Settings, 
  LogOut, 
  Menu, 
  X, 
  User,
  ChevronDown,
  Target,
  Shield
} from 'lucide-react';
import { toast } from 'sonner';

const Header = ({ variant = 'default' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser, refreshUser } = useContext(UserContext);
  const { t, lang, setLanguage } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);

  // Check admin status when user is logged in
  useEffect(() => {
    const checkAdminStatus = async () => {
      if (!user) {
        setIsAdmin(false);
        return;
      }
      try {
        const response = await adminProfitabilityAPI.verifyAccess();
        setIsAdmin(response.data?.is_admin === true);
      } catch (error) {
        // Not an admin or error checking
        setIsAdmin(false);
      }
    };
    checkAdminStatus();
  }, [user]);

  const navItems = [
    { path: '/dashboard', label: t('nav_dashboard'), icon: LayoutDashboard },
    { path: '/analyze', label: t('nav_analyze'), icon: Search, confirmIfActive: true },
    { path: '/saved', label: t('nav_saved'), icon: Bookmark },
    { path: '/compare', label: t('nav_compare'), icon: GitCompare },
    { path: '/inbox', label: t('nav_inbox'), icon: Inbox },
    { path: '/criteria', label: t('nav_criteria'), icon: Target },
  ];

  // Check if user has an active analysis
  const hasActiveAnalysis = () => {
    try {
      const saved = sessionStorage.getItem('chekdeal_last_analysis');
      return saved && saved !== 'null';
    } catch {
      return false;
    }
  };

  // Handle nav click with confirmation for Analyze Deal when there's active work
  const handleNavClick = (item) => {
    if (item.confirmIfActive && item.path === '/analyze' && location.pathname.startsWith('/results') && hasActiveAnalysis()) {
      const confirmed = window.confirm(
        lang === 'en' 
          ? 'Starting a new analysis will clear your current inputs. Continue?' 
          : '¿Iniciar un nuevo análisis borrará tus entradas actuales. ¿Continuar?'
      );
      if (!confirmed) return;
    }
    navigate(item.path);
  };

  // Sign-in opens the card with the Google buttons AND the consent line (Terms of
  // Service / Privacy Policy) — it used to jump straight to Google, with nothing said.
  // The provider (emergent | google) is still resolved at runtime by startLogin, from
  // inside AuthPrompt; after auth the visitor lands back on the page they were on.
  const handleGoogleLogin = () => setSignInOpen(true);
  const signInDialog = (
    <SignInDialog
      open={signInOpen}
      onClose={() => setSignInOpen(false)}
      lang={lang}
      redirectAfterAuth={location.pathname || '/analyze'}
    />
  );

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await authAPI.logout();
      setUser(null);
      navigate('/');
      toast.success(t('msg_logout_success') || (lang === 'en' ? 'Logged out successfully' : 'Sesión cerrada'));
    } catch (error) {
      console.error('Logout failed:', error);
      setUser(null);
      navigate('/');
    } finally {
      setLoggingOut(false);
      setUserMenuOpen(false);
    }
  };

  const isActivePath = (path) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Landing page header (minimal)
  if (variant === 'landing') {
    return (
      <header className="fixed top-0 w-full backdrop-blur-xl bg-white/80 border-b border-border z-50">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          {/* Logo */}
          <button onClick={() => navigate('/')} className="flex items-center gap-2" data-testid="nav-home">
            {/* The brand mark. What stood here was a generic lucide "Home" glyph in a
                coloured square — a placeholder, not a logo. The wordmark stays as text
                beside it: the horizontal lockup already contains the word, so pairing
                the two would print CHEKSTE twice. h-8 matches the square it replaces;
                width follows the 1.30 aspect ratio so nothing reflows. */}
            <img
              src={cheksteSymbol}
              alt=""
              aria-hidden="true"
              className="h-8 w-auto"
            />
            <span className="text-xl sm:text-2xl font-bold tracking-tight text-primary">
              CHEKSTE<span className="text-[0.4em] font-normal relative top-[1.1em]">™</span>
            </span>
          </button>

          {/* Right side */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Language selector */}
            <select 
              value={lang} 
              onChange={(e) => setLanguage(e.target.value)}
              className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-md border border-border bg-white text-sm"
              data-testid="lang-select"
            >
              <option value="en">English</option>
              <option value="es">Español</option>
            </select>

            {user ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-100 transition-colors"
                  data-testid="user-menu-btn"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-8 h-8 rounded-full" />
                  ) : (
                    <div className="w-8 h-8 bg-secondary/10 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-secondary" />
                    </div>
                  )}
                  <span className="hidden sm:block text-sm font-medium truncate max-w-[120px]">
                    {user.name || user.email?.split('@')[0]}
                  </span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </button>

                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-border z-50 py-1">
                      <button
                        onClick={() => { navigate('/dashboard'); setUserMenuOpen(false); }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                      >
                        <LayoutDashboard className="w-4 h-4" />
                        {lang === 'en' ? 'Dashboard' : 'Panel'}
                      </button>
                      <button
                        onClick={() => { navigate('/settings'); setUserMenuOpen(false); }}
                        className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
                      >
                        <Settings className="w-4 h-4" />
                        {lang === 'en' ? 'Settings' : 'Configuración'}
                      </button>
                      {/* Admin link - only visible for admin/founder users */}
                      {isAdmin && (
                        <button
                          onClick={() => { navigate('/admin'); setUserMenuOpen(false); }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-purple-50 flex items-center gap-2 text-purple-700"
                          data-testid="admin-link"
                        >
                          <Shield className="w-4 h-4" />
                          {lang === 'en' ? 'Admin Analytics' : 'Admin Analytics'}
                        </button>
                      )}
                      <hr className="my-1 border-border" />
                      <button
                        onClick={handleLogout}
                        disabled={loggingOut}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                      >
                        <LogOut className="w-4 h-4" />
                        {loggingOut ? '...' : (lang === 'en' ? 'Sign Out' : 'Cerrar Sesión')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <>
                <button
                  onClick={handleGoogleLogin}
                  className="hidden sm:block px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors"
                  data-testid="signin-btn"
                >
                  {t('cta_login')}
                </button>
                <button
                  onClick={() => navigate('/analyze')}
                  className="px-3 sm:px-4 py-2 bg-secondary text-white rounded-md font-medium hover:bg-blue-700 transition-all shadow-sm active:scale-95 text-sm"
                  data-testid="get-started-btn"
                >
                  {t('cta_get_started')}
                </button>
              </>
            )}
          </div>
        </div>
        {signInDialog}
      </header>
    );
  }

  // Default header for logged-in pages
  return (
    <header className="backdrop-blur-xl bg-white/80 border-b border-border sticky top-0 z-50">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between py-3 sm:py-4">
          {/* Logo */}
          <button onClick={() => navigate('/')} className="flex items-center gap-2" data-testid="nav-home">
            {/* The brand mark. What stood here was a generic lucide "Home" glyph in a
                coloured square — a placeholder, not a logo. The wordmark stays as text
                beside it: the horizontal lockup already contains the word, so pairing
                the two would print CHEKSTE twice. h-8 matches the square it replaces;
                width follows the 1.30 aspect ratio so nothing reflows. */}
            <img
              src={cheksteSymbol}
              alt=""
              aria-hidden="true"
              className="h-8 w-auto"
            />
            <span className="text-xl sm:text-2xl font-bold tracking-tight text-primary">
              CHEKSTE<span className="text-[0.4em] font-normal relative top-[1.1em]">™</span>
            </span>
          </button>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActivePath(item.path);
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavClick(item)}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-secondary/10 text-secondary'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <Icon className="w-4 h-4" />
                  {item.label}
                </button>
              );
            })}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Usage Counter (logged-in users only) */}
            {user && <CompactUsageCounter />}
            
            {/* Language (desktop) */}
            <select 
              value={lang} 
              onChange={(e) => setLanguage(e.target.value)}
              className="hidden sm:block px-2 py-1.5 rounded-md border border-border bg-white text-sm"
              data-testid="lang-select"
            >
              <option value="en">EN</option>
              <option value="es">ES</option>
            </select>

            {user ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg hover:bg-slate-100 transition-colors border border-transparent hover:border-border"
                  data-testid="user-menu-btn"
                >
                  {user.picture ? (
                    <img src={user.picture} alt="" className="w-7 h-7 sm:w-8 sm:h-8 rounded-full" />
                  ) : (
                    <div className="w-7 h-7 sm:w-8 sm:h-8 bg-secondary/10 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-secondary" />
                    </div>
                  )}
                  <span className="hidden md:block text-sm font-medium truncate max-w-[100px]">
                    {user.name?.split(' ')[0] || user.email?.split('@')[0]}
                  </span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground hidden sm:block" />
                </button>

                {userMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setUserMenuOpen(false)} />
                    <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-lg border border-border z-50 py-2">
                      {/* User info */}
                      <div className="px-4 py-2 border-b border-border">
                        <p className="text-sm font-medium text-primary truncate">{user.name || 'User'}</p>
                        <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${
                            user.plan_id === 'FOUNDER' ? 'bg-purple-100 text-purple-700' :
                            user.plan_id === 'PRO_PLUS' ? 'bg-blue-100 text-blue-700' :
                            user.plan_id === 'PRO' ? 'bg-emerald-100 text-emerald-700' :
                            'bg-slate-100 text-slate-700'
                          }`}>
                            {user.plan_id === 'PRO' ? 'Investor' :
                             user.plan_id === 'PRO_PLUS' ? 'Pro' :
                             user.plan_id === 'FOUNDER' ? 'Founder' : 'Free'}
                          </span>
                          {isAdmin && (
                            <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                              Admin
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Menu items */}
                      <div className="py-1">
                        <button
                          onClick={() => { navigate('/billing'); setUserMenuOpen(false); }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-3"
                        >
                          <CreditCard className="w-4 h-4 text-muted-foreground" />
                          {lang === 'en' ? 'Billing' : 'Facturación'}
                        </button>
                        <button
                          onClick={() => { navigate('/settings'); setUserMenuOpen(false); }}
                          className="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-3"
                        >
                          <Settings className="w-4 h-4 text-muted-foreground" />
                          {lang === 'en' ? 'Settings' : 'Configuración'}
                        </button>
                        
                        {/* Admin link - only visible for admin/founder users */}
                        {isAdmin && (
                          <button
                            onClick={() => { navigate('/admin'); setUserMenuOpen(false); }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-purple-50 flex items-center gap-3 text-purple-700 font-medium"
                            data-testid="admin-dashboard-link"
                          >
                            <Shield className="w-4 h-4" />
                            {lang === 'en' ? 'Admin Analytics' : 'Admin Analytics'}
                          </button>
                        )}
                      </div>

                      <hr className="my-1 border-border" />
                      
                      <button
                        onClick={handleLogout}
                        disabled={loggingOut}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-3"
                        data-testid="logout-btn"
                      >
                        <LogOut className="w-4 h-4" />
                        {loggingOut ? '...' : (lang === 'en' ? 'Sign Out' : 'Cerrar Sesión')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <button
                onClick={handleGoogleLogin}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-secondary text-white rounded-lg font-medium hover:bg-blue-700 transition-all text-sm"
                data-testid="login-btn"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span className="hidden sm:inline">{lang === 'en' ? 'Sign In' : 'Iniciar Sesión'}</span>
              </button>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors"
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-border py-4 pb-6">
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = isActivePath(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => { handleNavClick(item); setMobileMenuOpen(false); }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                      isActive
                        ? 'bg-secondary/10 text-secondary'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    {item.label}
                  </button>
                );
              })}
            </nav>

            {/* Mobile language toggle */}
            <div className="mt-4 px-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">
                  {lang === 'en' ? 'Language:' : 'Idioma:'}
                </span>
                <button
                  onClick={() => setLanguage(lang === 'en' ? 'es' : 'en')}
                  className="px-3 py-1 bg-slate-100 rounded text-sm font-medium"
                >
                  {lang === 'en' ? 'Español' : 'English'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      {signInDialog}
    </header>
  );
};

export default Header;
